import InfoOutlined from "@mui/icons-material/InfoOutlined";
import React from "react";
import { useTranslation } from "react-i18next";
import { NumericFormat, NumericFormatProps } from "react-number-format";
import ErrorDisplayTableRow from "../../components/ErrorDisplay/ErrorDisplayTableRow";
import TableHeader from "../../components/Headers/TableHeader";
import NoResultsTableRow from "../../components/NoResults/NoResultsTableRow";
import SkeletonTableBody from "../../components/Skeletons/SkeletonTableBody";
import SkeletonTableHeader from "../../components/Skeletons/SkeletonTableHeader";
import Tooltip from "../../components/Tooltip/Tooltip";
import { useClientConfiguration } from "../../data/global/useProductConfiguration";
import { usePayerLandscapeState } from "../../data/payerLandscape/hooks";
import { MarketSummary } from "../../generate";

type DataKey = keyof MarketSummary;

interface Props {
  client: string;
  loading: boolean;
  marketData?: MarketSummary[] | null;
  error?: null | Error;
  displayKeys?: Array<keyof MarketSummary>;
}

//HEADER
const MarketSummaryHeaders = ({ marketData }: { marketData?: MarketSummary[] | null }) => {
  return (
    <thead className="bg-rules">
      <tr>
        <th className="p-3"></th>
        {marketData &&
          marketData.map((item) => (
            <th key={item.product} className="p-3">
              {item.product}
            </th>
          ))}
      </tr>
    </thead>
  );
};

//ROW
const MarketSummaryRow = ({
  dataKey,
  label,
  format,
  align = "left",
  marketData,
}: {
  dataKey: DataKey;
  label: string;
  format?: NumericFormatProps;
  align?: string;
  marketData?: MarketSummary[] | null;
}) => (
  <tr>
    <td>
      <div className="flex-grow font-bold sm:font-normal text-center sm:text-left">{label}</div>
    </td>
    {marketData &&
      marketData.map((item) => (
        <td className={`text-${align}`} key={item.product} data-label={item.product}>
          {format && <NumericFormat value={item[dataKey]} displayType={"text"} {...format} />}
          {!format && item[dataKey]}
        </td>
      ))}
  </tr>
);

interface THeadProps {
  loading: boolean;
  error: null | Error | undefined;
  marketData: MarketSummary[] | null | undefined;
}

const THead: React.FC<THeadProps> = ({ loading, error, marketData }) => {
  if (loading) {
    return <SkeletonTableHeader columnCount={6} />;
  }
  if (error || (marketData && marketData.length === 0)) {
    return (
      <thead>
        <tr>
          <th className="p-3">
            <div className="h-5"></div>
          </th>
        </tr>
      </thead>
    );
  }
  return <MarketSummaryHeaders marketData={marketData} />;
};

interface TBodyProps {
  loading: boolean;
  error: null | Error | undefined;
  marketData: MarketSummary[] | null | undefined;
  displayKeys: Array<keyof MarketSummary>;
  client: string;
  timePeriod: string;
  tbodyHeight: number;
}

const TBody: React.FC<TBodyProps> = ({ loading, error, marketData, displayKeys, client, timePeriod, tbodyHeight }) => {
  const { t } = useTranslation();
  if (loading) {
    return <SkeletonTableBody columnCount={6} rowCount={4} />;
  }
  if (error) {
    return <ErrorDisplayTableRow columnCount={1} height={tbodyHeight} error={error} />;
  }
  if (marketData && marketData.length === 0) {
    return <NoResultsTableRow columnCount={1} height={tbodyHeight} />;
  }
  return (
    <tbody>
      {displayKeys.includes("coverageStatus") && (
        <MarketSummaryRow
          dataKey={"coverageStatus"}
          label={t([`planProfile.coverageStatus.${client}`, `planProfile.coverageStatus.default`])}
          marketData={marketData}
        />
      )}
      {displayKeys.includes("relativeStatus") && (
        <MarketSummaryRow
          dataKey={"relativeStatus"}
          label={t([`planProfile.relativeStatus.${client}`, `planProfile.relativeStatus.default`])}
          marketData={marketData}
        />
      )}
      {displayKeys.includes("tRx") && (
        <MarketSummaryRow
          dataKey={"tRx"}
          label={t([`planProfile.productTrx.${timePeriod}.${client}`, `planProfile.productTrx.${timePeriod}.default`])}
          format={{ thousandSeparator: true }}
          marketData={marketData}
        />
      )}
      {displayKeys.includes("prodTRx") && (
        <MarketSummaryRow
          dataKey={"prodTRx"}
          label={t([`planProfile.productTrx.${timePeriod}.${client}`, `planProfile.productTrx.${timePeriod}.default`])}
          format={{ thousandSeparator: true }}
          marketData={marketData}
        />
      )}
    </tbody>
  );
};

const MarketSummaryTable: React.FC<Props> = ({
  client,
  marketData,
  displayKeys = ["coverageStatus", "relativeStatus", "tRx", "marketShare", "prodTRx"],
  loading,
  error,
}) => {
  const { t } = useTranslation();
  const {
    sharedFilters: { timePeriod },
  } = usePayerLandscapeState();
  const rowHeight = 38;
  const rowCount = 4;
  const tbodyHeight = Math.ceil(rowHeight * rowCount);

  const config = useClientConfiguration(client);
  const { hideMarketSummaryTooltip } = config;

  return (
    <div className="pb-6">
      <TableHeader title={t("planProfile.marketSummary")}>
        {!hideMarketSummaryTooltip && (
          <Tooltip
            placement="bottom-end"
            tip={
              t([`planProfile.marketSummaryTooltip.${client}`, `planProfile.marketSummaryTooltip.default`]) as string
            }
          >
            <InfoOutlined className="text-primary" />
          </Tooltip>
        )}
      </TableHeader>
      <table className="w-full  border-rules sm:border-2">
        <THead loading={loading} error={error} marketData={marketData} />
        <TBody
          loading={loading}
          error={error}
          marketData={marketData}
          displayKeys={displayKeys}
          client={client}
          timePeriod={timePeriod ?? ""}
          tbodyHeight={tbodyHeight}
        />
      </table>
    </div>
  );
};

export default MarketSummaryTable;
